import { useEffect, useState, useCallback, useRef } from 'react';
import { X, Upload, CheckCircle, Clock } from 'lucide-react';
import { getCSRFToken } from '../../utils/csrf';

interface ReceiptItem {
  date: string;
  type_label: string;
  entity_label: string | null;
  amount: number;
}

interface TransferReceipt {
  original_filename: string;
  uploaded_at: string;
  verified_at: string | null;
}

interface ReceiptData {
  id: number;
  description: string;
  transaction_description: string;
  date: string;
  items: ReceiptItem[];
  total: number;
  transfer_receipt: TransferReceipt | null;
}

interface ReceiptModalProps {
  receiptId: number;
  onClose: () => void;
}

const currencyFormatter = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function formatCurrency(value: number): string {
  return currencyFormatter.format(value);
}

export function ReceiptModal({ receiptId, onClose }: ReceiptModalProps) {
  const [data, setData] = useState<ReceiptData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  useEffect(() => {
    fetch(`/cuenta-corriente/recibo/${receiptId}`)
      .then((res) => {
        if (!res.ok) throw new Error('No se pudo cargar el recibo');
        return res.json();
      })
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [receiptId]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);
    setUploadSuccess(false);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`/cuenta-corriente/recibo/${receiptId}/comprobante`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: {
          'X-XSRF-TOKEN': getCSRFToken(),
          'Accept': 'application/json',
        },
      });

      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error || json?.message || 'Error al subir el comprobante');
      }

      setUploadSuccess(true);
      // Refresh data
      const refreshRes = await fetch(`/cuenta-corriente/recibo/${receiptId}`);
      if (refreshRes.ok) {
        setData(await refreshRes.json());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-[fadeIn_150ms_ease-out]"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-xl bg-white shadow-xl animate-[slideUp_200ms_ease-out]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-800">
            Recibo #{receiptId}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <svg className="h-6 w-6 animate-spin text-green-500" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" opacity="0.25" />
                <path fill="currentColor" opacity="0.75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-lg bg-red-50 p-4 text-sm text-red-600">
              {error}
            </div>
          )}

          {uploadSuccess && (
            <div className="mb-4 rounded-lg bg-green-50 p-4 text-sm text-green-600">
              Comprobante subido correctamente.
            </div>
          )}

          {data && (
            <>
              <div className="mb-4 space-y-1 text-sm text-gray-600">
                <p><span className="font-medium text-gray-700">Fecha:</span> {data.date}</p>
                {data.transaction_description && (
                  <p>{data.transaction_description}</p>
                )}
                {data.description && data.description !== data.transaction_description && (
                  <p>{data.description}</p>
                )}
              </div>

              <h3 className="mb-2 text-sm font-semibold text-gray-700">Movimientos asociados</h3>
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <th className="px-3 py-2 text-left">Fecha</th>
                      <th className="px-3 py-2 text-left">Tipo / #</th>
                      <th className="px-3 py-2 text-right">Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((item, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="px-3 py-2 text-gray-600">{item.date}</td>
                        <td className="px-3 py-2 text-gray-800">
                          {item.type_label}
                          {item.entity_label && <span className="ml-1 text-gray-500">{item.entity_label}</span>}
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-gray-800">
                          {formatCurrency(item.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 flex justify-end border-t border-gray-100 pt-3">
                <span className="text-sm font-semibold text-gray-800">
                  Total: {formatCurrency(data.total)}
                </span>
              </div>

              {/* Transfer receipt section */}
              <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h3 className="mb-2 text-sm font-semibold text-gray-700">Comprobante de transferencia</h3>
                {data.transfer_receipt ? (
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-600">
                      <span className="font-medium">{data.transfer_receipt.original_filename}</span>
                      {' — '}subido el {data.transfer_receipt.uploaded_at}
                    </p>
                    {data.transfer_receipt.verified_at ? (
                      <div className="flex items-center gap-1.5 text-green-600">
                        <CheckCircle className="h-4 w-4" />
                        <span>Verificada el {data.transfer_receipt.verified_at}</span>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-1.5 text-amber-600">
                          <Clock className="h-4 w-4" />
                          <span>Pendiente de verificación</span>
                        </div>
                        <label className="mt-2 inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm ring-1 ring-gray-300 transition-colors hover:bg-gray-50">
                          <Upload className="h-3.5 w-3.5" />
                          Reemplazar comprobante
                          <input
                            ref={fileInputRef}
                            type="file"
                            className="hidden"
                            accept="image/jpeg,image/png,image/webp,application/pdf"
                            onChange={handleUpload}
                            disabled={uploading}
                          />
                        </label>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-500">No se adjuntó comprobante.</p>
                    <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-green-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-green-600">
                      <Upload className="h-3.5 w-3.5" />
                      {uploading ? 'Subiendo...' : 'Subir comprobante'}
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept="image/jpeg,image/png,image/webp,application/pdf"
                        onChange={handleUpload}
                        disabled={uploading}
                      />
                    </label>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-5 py-3">
          <button
            onClick={onClose}
            className="w-full rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
